import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { RoomType } from "../../types/C1_RoomTypes";
import { C1_ELO_PLAN_MODULE_SIZE } from "../../lib/C1_planSymbolSizes";
import { c1_state } from "../../model/C1_runtimeContext";
import { useOnChangeWatcher } from "../../../../src/models/useOnChangeWatcher";
import { C1_FurnitureSvg, C1_FurnitureUprightContent } from "../C1_FurnitureSvg";

/** Shared front view for the palette icon and the plan graphic. */
function WallRadiatorSymbol({ width, height, strokeColor, fillColor, strokeWidth }: {
  width: number; height: number; strokeColor: string; fillColor: string; strokeWidth: number;
}) {
  const outline = Math.min(strokeWidth, height * 0.12);
  const tubeWidth = width / 8;
  const barHeight = tubeWidth * 0.5;
  const overhang = height * 0.025;
  const towelLeft = width * 0.18;
  const towelRight = width * 0.68;
  // Cover the upper bar's outline as well as its face beneath the towel.
  const towelTop = (height - barHeight) / 2 - outline * 0.5;
  const towelBottom = height - overhang / 2;
  return (
    <g stroke={strokeColor} strokeLinecap="butt" strokeLinejoin="miter">
      {[0, width - tubeWidth].map(x => (
        <rect key={x} x={x} y={0} width={tubeWidth} height={height} fill={fillColor}
          style={{ strokeWidth: outline }} />
      ))}
      {Array.from({ length: 5 }, (_, index) => overhang + (height - overhang * 2 - barHeight) * index / 4).map(y => (
        <rect key={y} x={tubeWidth} y={y} width={width - tubeWidth * 2} height={barHeight} fill={fillColor}
          style={{ strokeWidth: outline * 0.5 }} />
      ))}
      <g style={{ strokeWidth: outline * 0.5 }}>
        <path
          d={`M ${towelLeft} ${towelTop} H ${towelRight} V ${towelBottom} Q ${width * 0.555} ${towelBottom - overhang} ${width * 0.43} ${towelBottom} Q ${width * 0.305} ${towelBottom + overhang} ${towelLeft} ${towelBottom} Z`}
          fill={fillColor}
        />
        {[0.28, 0.58].map(position => (
          <path key={position}
            d={`M ${width * position} ${towelTop + barHeight * 2} V ${towelBottom - barHeight}`}
            fill="none" style={{ strokeWidth: outline * 0.3 }} />
        ))}
      </g>
    </g>
  );
}

function WallRadiatorRenderer({
  width, height, rotation = 0, PX, strokeWidth = 2, strokeColor = "#000000", fillColor = "#ffffff",
  ownerRotation = 0, ownerReflect = false,
}: C1_FurnitureRendererProps) {
  useOnChangeWatcher(c1_state.editMode);
  const electricalMode = c1_state.editMode.value === "EloConcept" ||
    c1_state.editMode.value === "EloDetail";
  const frameColor = electricalMode && strokeColor !== "orange" && strokeColor !== "#ffc766"
    ? "#94a3b8" : strokeColor;
  const frameWidth = electricalMode ? Math.max(1, strokeWidth) : strokeWidth;
  const selected = strokeColor === "orange";
  const indirectlySelected = strokeColor === "#ffc766";
  const iconOutlineColor = selected ? "#f59e0b" : indirectlySelected ? "#fdba74" : "#94a3b8";
  const radius = C1_ELO_PLAN_MODULE_SIZE / 2;
  const centerX = width / 2;
  const centerY = height + PX * 0.04 + radius;
  const iconSize = radius * 1.5;
  return (
    <C1_FurnitureSvg width={width} height={height} rotation={rotation} PX={PX} viewBoxWidth={width} viewBoxHeight={height}
      originXPercentage={50} originYPercentage={-100}>
      <path d={`M ${width / 3} ${-0.05 * PX} V ${height} M ${width * 2 / 3} ${-0.05 * PX} V ${height}`}
        fill="none" stroke={frameColor} strokeWidth={electricalMode ? frameWidth : Math.min(strokeWidth, height * 0.12)} />
      <rect x={0} y={0} width={width} height={height} rx={height / 2}
        fill={fillColor} stroke={frameColor} strokeWidth={frameWidth} />
      <g>
        <circle cx={centerX} cy={centerY} r={radius}
          fill={selected || indirectlySelected ? iconOutlineColor : "#ffffff"} fillOpacity={0.5}
          stroke={iconOutlineColor} strokeWidth={selected ? 1.4 : indirectlySelected ? 1.1 : 0.6} />
        <C1_FurnitureUprightContent anchorX={centerX} anchorY={centerY}
          ownerRotation={Number(ownerRotation)} ownerReflect={Boolean(ownerReflect)}>
          <svg x={centerX - iconSize / 2} y={centerY - iconSize / 2} width={iconSize} height={iconSize}
            viewBox="0 0 24 24" aria-hidden="true">
            <g transform="translate(4 2)">
              <WallRadiatorSymbol width={16} height={20} strokeColor="#000000" fillColor="#ffffff" strokeWidth={1} />
            </g>
          </svg>
        </C1_FurnitureUprightContent>
      </g>
    </C1_FurnitureSvg>
  );
}

const WallRadiator: C1_FurnitureDefinition = {
  id: "wallRadiator",
  description: { de: "Wandheizkörper", en: "Wall radiator" },
  category: RoomType.Bathroom,
  defaultWidth: 0.6,
  defaultHeight: 0.05,
  originXPercentage: 50,
  // Fixed 5 cm depth and 5 cm clearance: anchor sits on the wall between the mounts.
  originYPercentage: -100,
  wallDockable: true,
  autoRotate: true,
  grabsW: true,
  grabsH: false,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeLinecap="butt" strokeLinejoin="miter">
      <g transform="translate(4 2)">
        <WallRadiatorSymbol width={16} height={20} strokeColor="currentColor" fillColor="#ffffff" strokeWidth={1} />
      </g>
    </svg>
  ),
  Renderer: WallRadiatorRenderer,
};

export default WallRadiator;
